import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, GraduationCap, ArrowUpRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const studentId = (student) => student?.id || student?.student_id || "";

const Promotion = () => {
  const { user } = useAuth();
  const effectiveCapabilities = user?.effective_capabilities || [];
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const isSuperAdmin = userRoles.includes("super_admin");
  const hasEffectiveCapability = (capability) =>
    isSuperAdmin || effectiveCapabilities.includes(capability);
  const navigate = useNavigate();

  const canPromote =
    hasEffectiveCapability("promotion.execute");

  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);

  const [source, setSource] = useState({
    session_id: "",
    program_id: "",
    class_id: "",
    arm_id: "",
  });

  const [target, setTarget] = useState({
    session_id: "",
    program_id: "",
    class_id: "",
    arm_id: "",
  });

  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promotionResult, setPromotionResult] = useState(null);
  const [studentSearch, setStudentSearch] = useState("");

  const sourceProgram = programs.find((p) => p.id === source.program_id);
  const sourceClasses = sourceProgram?.classes || [];
  const sourceClass = sourceClasses.find((c) => c.id === source.class_id);
  const sourceArms = sourceClass?.arms || [];

  const targetProgram = programs.find((p) => p.id === target.program_id);
  const targetClasses = targetProgram?.classes || [];
  const targetClass = targetClasses.find((c) => c.id === target.class_id);
  const targetArms = targetClass?.arms || [];
  const sourceArmName = sourceArms.find((arm) => arm.id === source.arm_id)?.name || "All arms";
  const targetArmName = targetArms.find((arm) => arm.id === target.arm_id)?.name || "No specific arm";
  const sourceSessionName = sessions.find((session) => session.id === source.session_id)?.name || "Selected session";
  const targetSessionName = sessions.find((session) => session.id === target.session_id)?.name || "Selected session";

  const visibleStudents = useMemo(() => {
    const query = studentSearch.trim().toLowerCase();
    if (!query) return students;
    return students.filter((student) => [student.full_name, student.first_name, student.last_name, student.admission_no]
      .filter(Boolean).join(" ").toLowerCase().includes(query));
  }, [studentSearch, students]);

  const selectedStudents = useMemo(
    () => students.filter((student) => selected[studentId(student)]),
    [students, selected],
  );

  const loadData = async () => {
    setLoading(true);

    try {
      const [programRes, sessionRes] = await Promise.all([
        api.get("/programs", { params: { active_only: true } }),
        api.get("/academic-sessions"),
      ]);

      const enrichedPrograms = await Promise.all(
        (programRes.data || []).map(async (program) => {
          const classRes = await api.get("/classes", {
            params: { program_id: program.id },
          });

          return {
            ...program,
            classes: classRes.data || [],
          };
        }),
      );

      setPrograms(enrichedPrograms);
      setSessions(sessionRes.data || []);

      const currentSession =
        (sessionRes.data || []).find((s) => s.is_current) ||
        (sessionRes.data || [])[0];

      if (currentSession) {
        setSource((prev) => ({
          ...prev,
          session_id: currentSession.id,
        }));
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setSource((prev) => ({
      ...prev,
      class_id: "",
      arm_id: "",
    }));
    setStudents([]);
    setSelected({});
  }, [source.program_id]);

  useEffect(() => {
    setSource((prev) => ({
      ...prev,
      arm_id: "",
    }));
  }, [source.class_id]);

  useEffect(() => {
    setTarget((prev) => ({
      ...prev,
      program_id: source.program_id,
      class_id: "",
      arm_id: "",
    }));
  }, [source.program_id]);

  useEffect(() => {
    setTarget((prev) => ({
      ...prev,
      arm_id: "",
    }));
  }, [target.class_id]);

  const loadStudents = useCallback(async () => {
    if (!source.program_id || !source.class_id) {
      setStudents([]);
      setSelected({});
      return;
    }

    setLoadingStudents(true);

    try {
      const params = {
        program_id: source.program_id,
        class_id: source.class_id,
        status: "active",
      };

      if (source.arm_id) {
        params.arm_id = source.arm_id;
      }

      if (source.session_id) {
        params.session_id = source.session_id;
      }

      const { data } = await api.get("/students", { params });

      const rows = Array.isArray(data) ? data : data.students || [];

      const normalized = rows
        .map((student) => {
          const sourceEnrollment = (student.programs || []).find(
            (enrollment) =>
              enrollment.program_id === source.program_id &&
              enrollment.class_id === source.class_id &&
              (!source.arm_id || enrollment.arm_id === source.arm_id) &&
              (!source.session_id || enrollment.session_id === source.session_id) &&
              enrollment.status === "active",
          );

          return {
            ...student,
            student_id: studentId(student),
            source_enrollment: sourceEnrollment || null,
          };
        })
        .filter((student) => student.student_id && student.source_enrollment);

      setStudents(normalized);
      setSelected({});
    } catch (error) {
      toast.error(formatApiError(error));
      setStudents([]);
      setSelected({});
    } finally {
      setLoadingStudents(false);
    }
  }, [source.arm_id, source.class_id, source.program_id, source.session_id]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const toggleStudent = (studentId) => {
    setSelected((prev) => ({
      ...prev,
      [studentId]: !prev[studentId],
    }));
  };

  const selectAll = () => {
    const next = {};
    visibleStudents.forEach((student) => {
      next[studentId(student)] = true;
    });
    setSelected(next);
  };

  const clearAll = () => {
    setSelected({});
  };

  const promoteSelected = async () => {
    if (!canPromote) {
      toast.error("You do not have permission to promote students.");
      return;
    }

    if (!source.program_id || !source.class_id) {
      toast.error("Select the current program and class.");
      return;
    }

    if (!target.session_id || !target.program_id || !target.class_id) {
      toast.error("Select the target academic session, program, and class.");
      return;
    }

    if (selectedStudents.length === 0) {
      toast.error("Select at least one student.");
      return;
    }

    if (
      source.session_id &&
      target.session_id === source.session_id &&
      target.program_id === source.program_id &&
      target.class_id === source.class_id &&
      (target.arm_id || "") === (source.arm_id || "")
    ) {
      toast.error("The target placement is the same as the current placement.");
      return;
    }

    const confirmed = window.confirm(
      [
        `Promote ${selectedStudents.length} selected student(s)?`,
        "",
        "From:",
        `${sourceSessionName} · ${sourceProgram?.name || "Selected program"} · ${sourceClass?.name || "Selected class"} · ${sourceArmName}`,
        "",
        "To:",
        `${targetSessionName} · ${targetProgram?.name || "Selected program"} · ${targetClass?.name || "Selected class"} · ${targetArmName}`,
      ].join("\n"),
    );

    if (!confirmed) return;

    setPromoting(true);

    try {
      const payload = {
        students: selectedStudents.map((student) => ({
          student_id: studentId(student),
          enrollment_id: student.source_enrollment.enrollment_id,
        })),
        source_program_id: source.program_id,
        source_class_id: source.class_id,
        source_arm_id: source.arm_id || null,
        source_session_id: source.session_id || null,
        to_program_id: target.program_id,
        to_class_id: target.class_id,
        to_arm_id: target.arm_id || null,
        to_session_id: target.session_id,
      };

      const { data } = await api.post(
        "/students/enrollments/bulk-promote",
        payload,
      );

      setPromotionResult(data);
      if (data.promoted_count) toast.success(`${data.promoted_count} student(s) promoted successfully.`);
      if (data.failed_count) toast.error(`${data.failed_count} student(s) could not be promoted. Review the results below.`);

      await loadStudents();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setPromoting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#9DCD3F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Student Promotion"
        description="Promote selected students to a new class and academic session."
        icon={<GraduationCap className="w-5 h-5" />}
      />

      {!canPromote && (
        <Card className="p-4 border-amber-200 bg-amber-50 text-amber-800">
          You have view-only access to student promotion.
        </Card>
      )}

      <Card className="p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-display text-lg text-[#1B2C5A]">
              Current Placement
            </h2>
            <p className="text-sm text-[#64748B]">
              Select the existing class whose students you want to review.
            </p>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium">Academic Session</label>
            <Select
              value={source.session_id}
              onValueChange={(value) =>
                setSource((prev) => ({ ...prev, session_id: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select session" />
              </SelectTrigger>
              <SelectContent>
                {sessions.map((session) => (
                  <SelectItem key={session.id} value={session.id}>
                    {session.name || session.label}
                    {session.is_current ? " (Current)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Program</label>
            <Select
              value={source.program_id}
              onValueChange={(value) =>
                setSource((prev) => ({ ...prev, program_id: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select program" />
              </SelectTrigger>
              <SelectContent>
                {programs.map((program) => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Class</label>
            <Select
              value={source.class_id}
              onValueChange={(value) =>
                setSource((prev) => ({ ...prev, class_id: value }))
              }
              disabled={!sourceProgram}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select class" />
              </SelectTrigger>
              <SelectContent>
                {sourceClasses.map((cls) => (
                  <SelectItem key={cls.id} value={cls.id}>
                    {cls.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Arm</label>
            <Select
              value={source.arm_id || "all"}
              onValueChange={(value) =>
                setSource((prev) => ({
                  ...prev,
                  arm_id: value === "all" ? "" : value,
                }))
              }
              disabled={!sourceClass || sourceArms.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder="All arms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All arms</SelectItem>
                {sourceArms.map((arm) => (
                  <SelectItem key={arm.id} value={arm.id}>
                    {arm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display text-lg text-[#1B2C5A]">Students</h2>
            <p className="text-sm text-[#64748B]">
              {students.length} student(s) found · {selectedStudents.length}{" "}
              selected
            </p>
          </div>

          <div className="flex gap-2">
            <Input
              value={studentSearch}
              onChange={(event) => setStudentSearch(event.target.value)}
              placeholder="Search student or admission no."
              className="w-56"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={selectAll}
              disabled={!visibleStudents.length}
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={clearAll}
              disabled={!selectedStudents.length}
            >
              Clear
            </Button>
          </div>
        </div>

        {loadingStudents ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#9DCD3F]" />
          </div>
        ) : students.length === 0 ? (
          <div className="py-12 text-center text-[#64748B]">
            Select a program and class to load active students.
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <div className="grid grid-cols-[48px_1fr_140px_140px_100px] bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <div />
              <div>Student</div>
              <div>Admission No.</div>
              <div>Current Class</div>
              <div>Status</div>
            </div>

            <div className="divide-y">
              {visibleStudents.map((student, index) => {
                const id = studentId(student);
                const checked = !!selected[id];

                return (
                  <label
                    key={id}
                    className={`grid grid-cols-[48px_1fr_140px_140px_100px] items-center px-4 py-3 cursor-pointer hover:bg-slate-50 ${
                      checked ? "bg-[#F7FBEF]" : ""
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleStudent(id)}
                    />

                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 w-5">
                        {index + 1}
                      </span>
                      <div>
                        <div className="font-medium text-[#1B2C5A]">
                          {student.full_name ||
                            `${student.first_name || ""} ${
                              student.last_name || ""
                            }`.trim() ||
                            "Unnamed Student"}
                        </div>
                      </div>
                    </div>

                    <div className="text-sm text-slate-600">
                      {student.admission_no || "—"}
                    </div>
                    <div className="text-sm text-slate-600">
                      {student.source_enrollment.class_name || sourceClass?.name || "Not assigned"}
                      {student.source_enrollment.arm_name ? ` / ${student.source_enrollment.arm_name}` : ""}
                    </div>
                    <div className="text-sm text-emerald-700">Active</div>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="mb-5">
          <h2 className="font-display text-lg text-[#1B2C5A]">
            Promotion Destination
          </h2>
          <p className="text-sm text-[#64748B]">
            Select where the checked students should be enrolled.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium">Academic Session</label>
            <Select
              value={target.session_id}
              onValueChange={(value) =>
                setTarget((prev) => ({ ...prev, session_id: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select target session" />
              </SelectTrigger>
              <SelectContent>
                {sessions.map((session) => (
                  <SelectItem key={session.id} value={session.id}>
                    {session.name || session.label}
                    {session.is_current ? " (Current)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Target Program</label>
            <Select
              value={target.program_id}
              onValueChange={(value) =>
                setTarget((prev) => ({ ...prev, program_id: value, class_id: "", arm_id: "" }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select target program" />
              </SelectTrigger>
              <SelectContent>
                {programs.map((program) => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Target Class</label>
            <Select
              value={target.class_id}
              onValueChange={(value) =>
                setTarget((prev) => ({ ...prev, class_id: value }))
              }
              disabled={!targetProgram}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select target class" />
              </SelectTrigger>
              <SelectContent>
                {targetClasses.map((cls) => (
                  <SelectItem key={cls.id} value={cls.id}>
                    {cls.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Target Arm</label>
            <Select
              value={target.arm_id || "none"}
              onValueChange={(value) =>
                setTarget((prev) => ({
                  ...prev,
                  arm_id: value === "none" ? "" : value,
                }))
              }
              disabled={!targetClass || targetArms.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder="No specific arm" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No specific arm</SelectItem>
                {targetArms.map((arm) => (
                  <SelectItem key={arm.id} value={arm.id}>
                    {arm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {sessions.filter((session) => session.id !== source.session_id).length === 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <span>No destination academic session is available. Create the next academic session first.</span>
            {canPromote && (
              <Button size="sm" variant="outline" onClick={() => navigate("/app/programs")}>
                Manage sessions
              </Button>
            )}
          </div>
        )}

        {selectedStudents.length > 0 && target.session_id && target.class_id && (
          <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <div className="font-semibold text-[#1B2C5A]">Promotion summary</div>
            <p className="mt-2">
              <strong>{selectedStudents.length}</strong> selected student(s) will transition from <strong>{sourceSessionName}</strong>, {sourceProgram?.name || "selected program"}, <strong>{sourceClass?.name || "selected class"}</strong> ({sourceArmName}) to <strong>{targetSessionName}</strong>, {targetProgram?.name || "selected program"}, <strong>{targetClass?.name || "selected class"}</strong> ({targetArmName}). Existing enrollment history is retained.
            </p>
          </div>
        )}

        {promotionResult && (
          <div className="mt-4 rounded-lg border border-slate-200 p-4 text-sm">
            <div className="font-semibold text-[#1B2C5A]">
              Last promotion: {promotionResult.promoted_count || 0} promoted, {promotionResult.failed_count || 0} failed
            </div>
            {(promotionResult.results || []).filter((result) => result.status === "failed").map((result) => (
              <p key={`${result.student_id}-${result.enrollment_id}`} className="mt-1 text-rose-700">
                {result.student_id}: {result.reason}
              </p>
            ))}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <Button
            onClick={promoteSelected}
            disabled={
              promoting ||
              !canPromote ||
              !selectedStudents.length ||
              !target.session_id ||
              !target.class_id
            }
            className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]"
          >
            {promoting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ArrowUpRight className="w-4 h-4 mr-2" />
            )}
            {promoting
              ? "Promoting..."
              : `Promote ${selectedStudents.length || ""} Selected`}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default Promotion;
